import React, { useEffect, useState } from 'react';
import ModalShell from './ModalShell';
import { haptic } from '../utils/vibration';
import { BookOpenText, Check, Clock, Newspaper, RefreshCcw, ShieldAlert, X } from 'lucide-react';

import { API_URL } from '../config';
import useModalBackButton from '../hooks/useModalBackButton';
import { formatTimezoneDate, TIMEZONE_CHANGED_EVENT } from '../utils/timezone';
import NewsMarkdown from './NewsMarkdown';

const formatDate = (value) => {
  const formatted = formatTimezoneDate(value, true);
  return formatted ? formatted.slice(0, 16) : '';
};

const CATEGORY_META = {
  news: {
    label: 'Новость',
    icon: Newspaper,
    badgeClassName: 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300'
  },
  instruction: {
    label: 'Инструкция',
    icon: BookOpenText,
    badgeClassName: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
  },
  update: {
    label: 'Обновление',
    icon: RefreshCcw,
    badgeClassName: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300'
  }
};

const resolveNewsImageUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^(https?:)?\/\//i.test(raw) || raw.startsWith('data:') || !raw.startsWith('/')) {
    return raw;
  }
  return `${API_URL}${raw}`;
};

const NewsDetailModal = ({
  news,
  isOpen,
  onClose,
  onConfirm,
  confirmLabel = 'Понятно',
  confirmIcon: ConfirmIcon = Check,
  loading = false,
  secondaryAction = null,
  canClose = true
}) => {
  useModalBackButton(isOpen && canClose, onClose);
  const [, setTimezoneVersion] = useState(0);

  useEffect(() => {
    const handleTimezoneChanged = () => {
      setTimezoneVersion((prev) => prev + 1);
    };

    window.addEventListener(TIMEZONE_CHANGED_EVENT, handleTimezoneChanged);
    return () => window.removeEventListener(TIMEZONE_CHANGED_EVENT, handleTimezoneChanged);
  }, []);

  if (!isOpen || !news) return null;
  const categoryMeta = CATEGORY_META[news.category || 'news'] || CATEGORY_META.news;
  const CategoryIcon = categoryMeta.icon;
  const isUpdate = (news.category || 'news') === 'update';
  const requiredUpdate = isUpdate && (news.update_is_required_now || news.is_required_update);
  const showUpdateProgress = loading && isUpdate;

  return (
    <ModalShell
      open={Boolean(isOpen && news)}
      onClose={canClose ? () => {
        haptic('close');
        onClose?.();
      } : undefined}
      closeOnOverlay={canClose}
      variant="window"
      panelClassName="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl"
    >
      <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-5 dark:border-gray-800">
        <div className="min-w-0">
          <div className={`mb-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${categoryMeta.badgeClassName}`}>
            <CategoryIcon className="h-3.5 w-3.5" />
            {categoryMeta.label}
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{news.title}</h2>
          <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <Clock className="h-3.5 w-3.5" />
            {formatDate(news.created_at)}
          </div>
        </div>
        {canClose ? (
          <button
            type="button"
            onClick={() => {
              haptic('close');
              onClose?.();
            }}
            className="rounded-xl p-2 text-gray-500 transition hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        ) : null}
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        {news.title_image_url && (
          <img src={resolveNewsImageUrl(news.title_image_url)} alt={news.title} className="mb-5 max-h-80 w-full rounded-3xl object-cover" />
        )}
        {showUpdateProgress ? (
          <div className="mb-5 rounded-2xl border border-blue-100 bg-blue-50/80 p-4 dark:border-blue-900/50 dark:bg-blue-950/25">
            <div className="flex items-center gap-4">
              <div className="pwa-update-loader shrink-0" aria-hidden="true">
                <RefreshCcw className="pwa-update-loader__icon h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Обновляем приложение</p>
                <p className="mt-1 text-xs leading-5 text-gray-600 dark:text-gray-300">
                  Готовим свежую версию и перезапускаем экран.
                </p>
                <div className="pwa-update-progress mt-3" aria-hidden="true" />
              </div>
            </div>
          </div>
        ) : null}
        {isUpdate ? (
          <div className="mb-5 flex flex-wrap gap-2">
            {news.app_version ? (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                Версия {news.app_version}
              </span>
            ) : null}
            {news.min_supported_version ? (
              <span className="inline-flex items-center rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700 dark:bg-orange-950/30 dark:text-orange-200">
                Минимальная {news.min_supported_version}
              </span>
            ) : null}
            {requiredUpdate ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 dark:bg-red-950/30 dark:text-red-200">
                <ShieldAlert className="h-3.5 w-3.5" />
                Обязательно
              </span>
            ) : null}
          </div>
        ) : null}
        <NewsMarkdown content={news.content} />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 p-5 dark:border-gray-800">
        {secondaryAction}
        <button
          type="button"
          onClick={() => {
            haptic('medium');
            onConfirm();
          }}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-60"
        >
          <ConfirmIcon className={`h-4 w-4 ${showUpdateProgress ? 'pwa-update-button-icon' : ''}`} />
          {confirmLabel}
        </button>
      </div>
    </ModalShell>
  );
};

export default NewsDetailModal;
